import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import CollapsableImageContent from "../components/CollapsableImageContent";
import FullscreenModal from "../components/FullscreenModal";
import InclusionDetail from "../components/InclusionDetail";
import { NavigationContext, UserContext } from "../contexts";
import { getPageTitle } from "../resources/routes";
import { Inclusion } from "../types";
import { scrollToTop } from "../util";

const TurnKeyInclusionsScreen = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [modalInclusion, setModalInclusion] = useState<Inclusion | null>(null);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);
    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedTurnKeyInclusion(null);
    });

    if (!userContext.selectedTurnKeyInclusion) {
      // If there are 'preselected' inclusions, select that the first one by default
      const preselected = userContext.turnKeyInclusionsWithPromoPricing.filter(
        (item) => item.preselected
      );
      if (preselected.length > 0) {
        onClickInclusion(preselected[0]);
      }
    }
  }, []);

  const onClickInclusion = (inclusion: Inclusion) => {
    if (
      userContext.selectedTurnKeyInclusion &&
      userContext.selectedTurnKeyInclusion.id === inclusion.id
    ) {
      userContext.setSelectedTurnKeyInclusion(null);
    } else {
      userContext.setSelectedTurnKeyInclusion(inclusion);
    }
  };

  const onClickMoreDetails = (inclusion: Inclusion) => {
    setModalInclusion(inclusion);
    setIsModalVisible(true);
  };

  const onModalClose = () => {
    setIsModalVisible(false);
    setIsModalClosing(false);
  };

  return (
    <div className={"screen screen-personalise-home"}>
      <div className={"container"}>
        <h2 className="h1">{getPageTitle(location.pathname)}</h2>
        <p className={"subtitle"}>
          All you need to do is turn the key and walk into your brand new home
          with nothing else to do!
        </p>

        <p className={"section-label"}>Select Your Upgrade Option</p>
      </div>
      <div className={"inclusions container"}>
        {userContext.turnKeyInclusionsWithPromoPricing.map((item) => {
          return (
            <CollapsableImageContent
              key={item.id}
              layout={"turn_key"}
              image={item.image}
              title={item.title}
              price={item.price}
              promo_price={item.promo_price}
              innerHtml={item.content}
              selected={
                userContext.selectedTurnKeyInclusion
                  ? userContext.selectedTurnKeyInclusion.id === item.id
                  : false
              }
              onClick={() => onClickInclusion(item)}
              onClickMoreDetails={() => onClickMoreDetails(item)}
            />
          );
        })}
      </div>
      {isModalVisible && modalInclusion !== null && (
        <FullscreenModal
          title={modalInclusion.title}
          closing={isModalClosing}
          onClose={onModalClose}
        >
          <InclusionDetail
            inclusion={modalInclusion}
            onClickBack={() => setIsModalClosing(true)}
          />
        </FullscreenModal>
      )}
    </div>
  );
});

export default TurnKeyInclusionsScreen;
